/**
 * W4's KnowIt adapter: connected IDE windows, dispatch writes, access grants, documentation
 * sync, the remote-session conversation store, and the first-run wizard.
 *
 * It is its own module rather than more of `knowitAutomation.ts` (already 1,384 lines) for the
 * same reason that file exists: these endpoints do not share one envelope. `ide-sessions.*`
 * and `ide-access.*` answer `{data}`, `ai-dispatch.*` answers a bare ad-hoc object per verb,
 * `doc-sync/*` answers bare typed records over REST **GET**s, and the conversation list is a
 * bare ARRAY. Each is pinned by a golden fixture in `knowitIde.test.ts` /
 * `knowitDocSync.test.ts`; nothing here assumes.
 *
 * **Six endpoint families are absent on purpose** (§E-7): `ide-sessions.heartbeat`,
 * `ai-dispatch.claim/.report/.heartbeat`, every `ide-runs.*` verb, and
 * `doc-sync/{push,resolve,changes,content,pull-ack}`. They are the extension's half of the
 * contract; a web caller pressing them would forge executor state. They are not
 * present-and-unused — the contract itself is the guard.
 *
 * **Never a raw `fetch`.** kms reaches the conversation store with `credentials:"include"`
 * because kms has a cookie session; slate carries a PKCE bearer, so a raw fetch arrives
 * ANONYMOUS. Every call here rides the injected, bearer-carrying transport.
 */

import {
  type ConversationDetail,
  ConversationDetailSchema,
  type ConversationDraft,
  type ConversationSummary,
  ConversationSummarySchema,
  type DispatchCreated,
  DispatchCreatedSchema,
  type DispatchDraft,
  type DispatchOutput,
  DispatchOutputSchema,
  type DocSyncLink,
  type DocSyncLinkFlags,
  DocSyncLinkSchema,
  type DocSyncStatePage,
  DocSyncStatePageSchema,
  type IdeGrant,
  type IdeGrantActivity,
  type IdeGrantApproval,
  type IdeGrantCandidate,
  IdeGrantCandidateSchema,
  type IdeGrantList,
  IdeGrantSchema,
  type IdeHistoryKind,
  type IdeSessionHistory,
  IdeSessionHistorySchema,
  type IdeSessionsPage,
  IdeSessionsPageSchema,
  type SeedWelcome,
  SeedWelcomeSchema,
  type TalkThread,
  TalkThreadSchema,
  type WizardCapabilities,
  WizardCapabilitiesSchema,
  type WizardPreflight,
} from "~/api/ideTypes";
import type { Dispatch } from "~/api/types";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : {};
}
function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** What this module needs from the class that owns the bearer. */
export interface IdeTransport {
  /** Outline-style dot-RPC POST. Returns the WHOLE parsed body — bare or `{data}`. */
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** The REST lane: `doc-sync/*` and `wizard.capabilities` are GETs, and the method matters. */
  get(path: string): Promise<unknown>;
  /** 204-tolerant REST, for the conversation store's POSTs. */
  rest(path: string, method: string, body?: Record<string, unknown>): Promise<unknown>;
}

/** The canonical path; `/api/tomatoflow/document-conversations` is only an `Order = -1` alias. */
const CONVERSATIONS = "document-conversations";

/** A preflight probe that answers "we could not tell" instead of hanging (§A.7 step 2). */
async function within<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), ms);
      }),
    ]);
  } catch {
    return null;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

export function createKnowItIde(transport: IdeTransport) {
  const sessions = {
    /**
     * Own rows only unless the caller is a workspace Admin — enforced server-side, and the
     * `policy.admin` flag is what lets the console say "you are seeing everyone's" rather than
     * leaving a colleague's window looking like one of yours.
     */
    list: async (
      options: { includeRevoked?: boolean; allWorkspaces?: boolean } = {},
    ): Promise<IdeSessionsPage> => {
      const raw = obj(
        await transport.post("ide-sessions.list", {
          ...(options.includeRevoked ? { includeRevoked: true } : {}),
          // The caller's own windows in EVERY workspace (a window is the person's, not the workspace's).
          ...(options.allWorkspaces ? { allWorkspaces: true } : {}),
        }),
      );
      const policy = obj(raw.policy);
      return IdeSessionsPageSchema.parse({
        sessions: arr(raw.data),
        admin: policy.admin === true,
        minimumAdvisedExtensionVersion:
          typeof policy.minimumAdvisedExtensionVersion === "string"
            ? policy.minimumAdvisedExtensionVersion
            : null,
      });
    },

    /**
     * Five bands, one request. History is deliberately NOT cached in a store: holding
     * somebody's prompts in memory after they navigated away is not a trade this feature
     * should make, so the only copy is React Query's, keyed by session and dropped with it.
     */
    history: async (
      sessionKey: string,
      options: { kinds?: IdeHistoryKind[]; since?: string; limit?: number } = {},
    ): Promise<IdeSessionHistory> => {
      const raw = obj(
        await transport.post("ide-sessions.history", {
          sessionKey,
          ...(options.kinds?.length ? { kinds: options.kinds } : {}),
          ...(options.since ? { since: options.since } : {}),
          ...(options.limit ? { limit: options.limit } : {}),
        }),
      );
      return IdeSessionHistorySchema.parse(obj(raw.data));
    },

    /** `{id}` for another's row, `{sessionKey}` for the self-disconnect path. */
    revoke: async (input: { id?: string; sessionKey?: string }): Promise<void> => {
      await transport.post("ide-sessions.revoke", {
        ...(input.id ? { id: input.id } : {}),
        ...(input.sessionKey ? { sessionKey: input.sessionKey } : {}),
      });
    },
  };

  const dispatches = {
    /**
     * The one write the web makes into a window. `originSessionKey` is never sent (D9) and the
     * privilege fields are never offered (D2) — see `DispatchDraft`.
     */
    create: async (input: DispatchDraft): Promise<DispatchCreated> => {
      const raw = obj(
        await transport.post("ai-dispatch.create", {
          kind: input.kind,
          agent: input.agent ?? "ClaudeCode",
          payload: input.payload,
          ...(input.targetSessionKey ? { targetSessionKey: input.targetSessionKey } : {}),
          ...(input.conversationId ? { conversationId: input.conversationId } : {}),
          ...(input.model ? { model: input.model } : {}),
          ...(input.effort ? { effort: input.effort } : {}),
          ...(input.tokenBudget ? { tokenBudget: input.tokenBudget } : {}),
          ...(input.ttlSeconds ? { ttlSeconds: input.ttlSeconds } : {}),
        }),
      );
      return DispatchCreatedSchema.parse({
        ...raw,
        status: typeof raw.status === "string" ? raw.status.toLowerCase() : "queued",
      });
    },

    /** D6: ONE row. A probe that reached for `.list` instead once pulled 59,058 characters. */
    get: async (id: string): Promise<Dispatch> => {
      const raw = obj(await transport.post("ai-dispatch.get", { id }));
      return toDispatch(obj(raw.dispatch));
    },

    cancel: async (input: { id: string; reason?: string }): Promise<void> => {
      await transport.post("ai-dispatch.cancel", {
        id: input.id,
        ...(input.reason?.trim() ? { reason: input.reason.trim() } : {}),
      });
    },

    /**
     * Which window each of the caller's conversations is held with — one row per conversation,
     * decided by its latest chat turn. A row the schema refuses is dropped rather than failing
     * the page: a thread with no known window is still a thread.
     */
    threads: async (): Promise<TalkThread[]> => {
      const raw = obj(await transport.post("ai-dispatch.threads", {}));
      return arr(raw.threads).flatMap((entry) => {
        const record = obj(entry);
        const parsed = TalkThreadSchema.safeParse({
          ...record,
          conversationId: typeof record.conversationId === "string" ? record.conversationId : "",
        });
        return parsed.success && parsed.data.conversationId ? [parsed.data] : [];
      });
    },

    /** The window's lines for one of the caller's own dispatches — the answer too long for `result`. */
    output: async (id: string, options: { after?: number } = {}): Promise<DispatchOutput> => {
      const raw = obj(
        await transport.post("ai-dispatch.output", {
          id,
          ...(options.after ? { after: options.after } : {}),
        }),
      );
      return DispatchOutputSchema.parse({
        ...raw,
        status: typeof raw.status === "string" ? raw.status.toLowerCase() : "queued",
      });
    },
  };

  const grants = {
    /** Works with the workspace switch OFF, by design: people must always see and kill grants. */
    list: async (): Promise<IdeGrantList> => {
      const raw = obj(await transport.post("ide-access.list", {}));
      const data = obj(raw.data);
      return {
        asOwner: arr(data.asOwner).map((entry) => IdeGrantSchema.parse(entry)),
        asGrantee: arr(data.asGrantee).map((entry) => IdeGrantSchema.parse(entry)),
      };
    },

    request: async (input: { targetUserId: string; purpose: string }): Promise<IdeGrant> => {
      const raw = obj(
        await transport.post("ide-access.request", {
          targetUserId: input.targetUserId,
          purpose: input.purpose,
        }),
      );
      return IdeGrantSchema.parse(obj(raw.data));
    },

    /**
     * Owner only — **admins never create or escalate** (G6). Widening an existing grant is
     * refused `409 widening-requires-fresh-consent`: the caller mints a new Pending request
     * rather than quietly expanding access.
     */
    approve: async (input: IdeGrantApproval): Promise<IdeGrant> => {
      const raw = obj(
        await transport.post("ide-access.approve", {
          grantId: input.grantId,
          level: input.level,
          ...(input.scope && (input.scope.machineId || input.scope.workspacePathPrefix)
            ? {
                scope: {
                  ...(input.scope.machineId ? { machineId: input.scope.machineId } : {}),
                  ...(input.scope.workspacePathPrefix
                    ? { workspacePathPrefix: input.scope.workspacePathPrefix }
                    : {}),
                },
              }
            : {}),
          ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
        }),
      );
      return IdeGrantSchema.parse(obj(raw.data));
    },

    decline: async (grantId: string): Promise<IdeGrant> => {
      const raw = obj(await transport.post("ide-access.decline", { grantId }));
      return IdeGrantSchema.parse(obj(raw.data));
    },

    /** Owner, grantee (self-renounce) or Admin; cancels in-flight work in the same transaction. */
    revoke: async (grantId: string): Promise<IdeGrant> => {
      const raw = obj(await transport.post("ide-access.revoke", { grantId }));
      return IdeGrantSchema.parse(obj(raw.data));
    },

    activity: async (grantId: string, limit?: number): Promise<IdeGrantActivity[]> => {
      const raw = obj(
        await transport.post("ide-access.activity", { grantId, ...(limit ? { limit } : {}) }),
      );
      return arr(obj(raw.data).items).map((entry) => obj(entry));
    },

    candidates: async (): Promise<IdeGrantCandidate[]> => {
      const raw = obj(await transport.post("ide-access.request-candidates", {}));
      return arr(raw.data).map((entry) => IdeGrantCandidateSchema.parse(entry));
    },
  };

  const docSync = {
    /** A GET. POSTing `doc-sync/links` is a different verb on a different route. */
    links: async (collectionId?: string): Promise<DocSyncLink[]> => {
      const query = collectionId ? `?collectionId=${encodeURIComponent(collectionId)}` : "";
      const raw = obj(await transport.get(`doc-sync/links${query}`));
      return arr(raw.links).map((entry) => DocSyncLinkSchema.parse(entry));
    },

    /**
     * The single write the web makes. `null` means KEEP for every nullable flag, so the whole
     * link is echoed back with exactly one flag changed — a bare `{linkId, enabled}` patch
     * would send `undefined` for `pullEnabled` and, on any server that reads it as `false`,
     * silently turn two-way sync off while somebody toggled something else.
     */
    updateLink: async (link: DocSyncLink, patch: DocSyncLinkFlags): Promise<DocSyncLink> => {
      const raw = await transport.post("doc-sync/link", {
        collectionId: link.collectionId,
        ...(link.parentDocumentId ? { parentDocumentId: link.parentDocumentId } : {}),
        syncPath: link.syncPath,
        ...(link.gitUrl ? { gitUrl: link.gitUrl } : {}),
        enabled: patch.enabled ?? link.enabled,
        autoRunFlow: link.autoRunFlow,
        pullEnabled: patch.pullEnabled ?? link.pullEnabled,
      });
      return DocSyncLinkSchema.parse(obj(raw));
    },

    state: async (linkId: string): Promise<DocSyncStatePage> => {
      const raw = await transport.get(`doc-sync/state?linkId=${encodeURIComponent(linkId)}`);
      return DocSyncStatePageSchema.parse(obj(raw));
    },
  };

  const conversations = {
    /** A BARE ARRAY — there is no envelope here to unwrap, and unwrapping one would blank it. */
    list: async (
      query: { documentId?: string; limit?: number } = {},
    ): Promise<ConversationSummary[]> => {
      const params = new URLSearchParams();
      if (query.documentId !== undefined) {
        params.set("documentId", query.documentId);
      }
      params.set("limit", String(query.limit ?? 50));
      const raw = await transport.get(`${CONVERSATIONS}?${params.toString()}`);
      return arr(raw).map((entry) => ConversationSummarySchema.parse(entry));
    },

    get: async (id: string): Promise<ConversationDetail> => {
      const raw = await transport.get(`${CONVERSATIONS}/${encodeURIComponent(id)}`);
      return ConversationDetailSchema.parse(obj(raw));
    },

    create: async (input: ConversationDraft): Promise<ConversationSummary> => {
      const raw = await transport.rest(CONVERSATIONS, "POST", {
        documentId: input.documentId ?? "",
        title: input.title,
        anchorType: input.anchorType ?? "Document",
        ...(input.flowId ? { flowId: input.flowId } : {}),
      });
      return ConversationSummarySchema.parse(obj(raw));
    },
  };

  const wizard = {
    capabilities: async (): Promise<WizardCapabilities> =>
      WizardCapabilitiesSchema.parse(obj(await transport.get("wizard.capabilities"))),

    /**
     * Two probes, 5 s each, neither allowed to throw. `operable` is their OR: a workspace with
     * a reachable local model needs no cloud catalogue, and one with a healthy catalogue needs
     * no Ollama.
     */
    preflight: async (): Promise<WizardPreflight> => {
      const [catalogRaw, localRaw] = await Promise.all([
        within(transport.get("thinkers/catalog/status"), 5000),
        within(transport.get("local-thinkers/health"), 5000),
      ]);
      const catalogBody = obj(catalogRaw);
      const localBody = obj(localRaw);
      const catalog = {
        status: typeof catalogBody.status === "string" ? catalogBody.status : "unknown",
        count: typeof catalogBody.count === "number" ? catalogBody.count : 0,
      };
      const local = {
        reachable: localBody.reachable === true,
        models: arr(localBody.models).filter((entry): entry is string => typeof entry === "string"),
      };
      return {
        catalog,
        local,
        operable:
          (catalog.status === "ok" && catalog.count > 0) ||
          (local.reachable && local.models.length > 0),
      };
    },

    seedWelcome: async (collectionId: string): Promise<SeedWelcome> =>
      SeedWelcomeSchema.parse(
        obj(await transport.post("collections.seed-welcome", { collectionId })),
      ),

    /**
     * Fire-and-forget telemetry. It must NEVER throw into the wizard — a rate limit (60/min)
     * or a bad deploy cannot be allowed to break somebody's first five minutes in the product.
     */
    recordEvent: async (name: string, payload?: Record<string, unknown>): Promise<void> => {
      try {
        await transport.post("events.record", { name, ...(payload ? { payload } : {}) });
      } catch {
        // Deliberately swallowed: see above.
      }
    },
  };

  return { ide: { sessions, dispatches, grants }, docSync, conversations, wizard };
}

/**
 * `ai-dispatch.get`'s single row, in the same shape `automation.dispatches.list` produces —
 * one `Dispatch` type, one mapping, so the list and the detail poll can never disagree about
 * what a dispatch is. `status` is lower-cased (slate convention); `statusDetail` is kept
 * VERBATIM, and `payload` stays a raw string because that is exactly what it is.
 */
function toDispatch(entry: Json): Dispatch {
  const str = (value: unknown): string => (typeof value === "string" ? value : "");
  const strOrNull = (value: unknown): string | null =>
    typeof value === "string" && value.length > 0 ? value : null;
  const run = entry.run ? obj(entry.run) : null;
  return {
    id: str(entry.id),
    kind: str(entry.kind),
    agent: str(entry.agent),
    status: str(entry.status).toLowerCase(),
    target: strOrNull(entry.targetSessionKey),
    claimedBy: strOrNull(entry.claimedBySessionKey),
    runId: strOrNull(entry.runId),
    createdAt: str(entry.createdAt) || new Date(0).toISOString(),
    claimedAt: strOrNull(entry.claimedAt),
    expiresAt: strOrNull(entry.expiresAt),
    completedAt: strOrNull(entry.completedAt),
    result: strOrNull(entry.result),
    error: strOrNull(entry.error),
    redacted: entry.redacted === true,
    originSessionKey: strOrNull(entry.originSessionKey),
    conversationId: strOrNull(entry.conversationId),
    tokenBudget: typeof entry.tokenBudget === "number" ? entry.tokenBudget : null,
    model: strOrNull(entry.model),
    effort: strOrNull(entry.effort),
    payload: strOrNull(entry.payload),
    isOwn: entry.isOwn !== false,
    statusDetail: strOrNull(entry.statusDetail),
    queuedBehind: typeof entry.queuedBehind === "number" ? entry.queuedBehind : 0,
    ownerUserName: strOrNull(entry.ownerUserName),
    run: run
      ? {
          status: str(run.status),
          currentNodeId: strOrNull(run.currentNodeId),
          awaitingApprovalNodeId: strOrNull(run.awaitingApprovalNodeId),
        }
      : null,
  };
}

/** Re-exported so the fake can build the same row shape from its own seeds. */
export { toDispatch as dispatchRowFrom };
